import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { QuestionnaireService } from '../../core/services/questionnaire.service';

// PrimeNG Components
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { Checkbox } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TooltipModule } from 'primeng/tooltip';
// Drag & Drop
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormDropdownService } from '../../core/services/form-dropdown.service';

@Component({
  selector: 'app-create-questionnaire',
  standalone: true,
  providers: [MessageService, ConfirmationService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    ButtonModule,
    DialogModule,
    SelectButtonModule,
    TagModule,
    TextareaModule,
    ToastModule,
    DragDropModule,
    Checkbox,
    ConfirmDialogModule,
    BreadcrumbModule,
    TooltipModule,
  ],
  templateUrl: './create-questionnaire.component.html',
  styleUrls: ['./create-questionnaire.component.scss'],
})
export class CreateQuestionnaireComponent implements OnInit {
  form: FormGroup;
  showTypeDialog = false;
  editingIndex: number | null = null;
  questionnaireId: string | null = null;

  // Modello per il Dialog (Configurazione campo)
  public fieldForm = {
    text: '',
    tooltip: '',
    type: '',
    mandatory: true,
    options: [] as { id: string; text: string; triggersAlert: boolean; requiresComment: boolean; position: number }[],
  };

  questionnaireTypes = [
    { label: 'Survey', value: 'SURVEY' },
    { label: 'Checklist', value: 'CHECKLIST' },
  ];

  questionTypes = [
    { label: 'Testo', value: 'TEXT', icon: 'pi pi-pencil' },
    { label: 'Numero', value: 'NUMBER', icon: 'pi pi-hashtag' },
    { label: 'Data', value: 'DATE', icon: 'pi pi-calendar' },
    { label: 'Scelta Singola', value: 'SINGLESELECT', icon: 'pi pi-list' },
    { label: 'Scelta Multipla', value: 'MULTISELECT', icon: 'pi pi-list-check' },
    { label: 'Checkbox', value: 'BOOLEAN', icon: 'pi pi-check-square' },
  ];

  trusteeTypes = signal<Array<{ name: string; code: string }>>([]);
  breadcrumbItems = signal<MenuItem[]>([]);
  private titleService = inject(Title);

  constructor(
    private fb: FormBuilder,
    private service: QuestionnaireService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private route: ActivatedRoute,
    private router: Router,
    private formDropdownService: FormDropdownService,
  ) {
    const today = new Date();
    const farFuture = new Date(2099, 0, 1);

    this.form = this.fb.group({
      name: ['', Validators.required],
      tooltip: [''],
      trusteeTypes: [[], Validators.required],
      validFrom: [today],
      validTo: [farFuture],
      questions: this.fb.array([], [Validators.required, Validators.minLength(1)]),
    });
  }

  ngOnInit(): void {
    // WCAG 9.2.4.2: Set descriptive page title
    this.titleService.setTitle('Crea Questionario - Portale Fiduciari AXA');

    // Controllo se siamo in modalità EDIT (presenza ID nell'URL)
    this.questionnaireId = this.route.snapshot.paramMap.get('id');
    
    // Set breadcrumb based on create/edit mode
    if (this.questionnaireId) {
      this.breadcrumbItems.set([
        { label: 'Questionari', routerLink: '/questionnaires' },
        { label: 'Modifica Questionario' },
      ]);
      this.titleService.setTitle('Modifica Questionario - Portale Fiduciari AXA');
    } else {
      this.breadcrumbItems.set([
        { label: 'Questionari', routerLink: '/questionnaires' },
        { label: 'Crea Questionario' },
      ]);
    }
    
    this.retriveTrusteeTypes();

    if (this.questionnaireId) {
      this.loadQuestionnaireData(this.questionnaireId);
    }
  }

  private loadQuestionnaireData(id: string) {
    this.service.getQuestionnaireById(id).subscribe({
      next: (data) => {
        this.form.patchValue({
          name: data.name,
          tooltip: data.tooltip ?? '',
          type: data.type,
          trusteeTypes: data.trusteeTypes || [],
          validFrom: data.validFrom ? new Date(data.validFrom) : null,
          validTo: data.validTo ? new Date(data.validTo) : null,
        });

        // Ricostruisco il FormArray delle domande (escludo quelle cancellate logicamente, ordino per position)
        this.questions.clear();
        data.questions
          .filter((q: any) => q.deleted !== true)
          .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0))
          .forEach((q: any) => {
          const optionsFBArray = this.fb.array(
            (q.options || []).filter((o: any) => o.deleted !== true).map((o: any, idx: number) =>
              this.fb.group({
                id: [o.id],
                text: [o.text],
                triggersAlert: [!!o.triggersAlert],
                requiresComment: [!!o.requiresComment],
                active: [o.active !== false], // preserva il flag cancellazione logica
                position: [o.position ?? idx + 1],
              }),
            ),
          );

          this.questions.push(
            this.fb.group({
              id: [q.id ?? null],   // necessario per upsert lato BE
              text: [q.text, Validators.required],
              tooltip: [q.tooltip ?? ''],
              type: [q.type, Validators.required],
              mandatory: [q.mandatory],
              position: [q.position],
              options: optionsFBArray,
            }),
          );
        });
      },
      error: (err) => {
        this.confirmationService.confirm({
          message: 'Caricamento fallito',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Ok',
          acceptButtonStyleClass: 'p-button-danger',
          rejectButtonStyleClass: 'p-button-text',
          accept: () => {
            // Dialog closes when Ok is clicked
          },
        });
      },
    });
  }

  // GETTERS
  get questions(): FormArray {
    return this.form.get('questions') as FormArray;
  }

  get canSubmit(): boolean {
    return this.form.get('name')?.value?.trim() !== '' && this.questions.length > 0;
  }

  getOptions(questionIndex: number): FormArray {
    return this.questions.at(questionIndex).get('options') as FormArray;
  }

  get isEditing(): boolean {
    return this.editingIndex !== null;
  }

  getQuestionTypeLabel(type: string): string {
    return this.questionTypes.find((questionType) => questionType.value === type)?.label ?? type;
  }

  // DIALOG ACTIONS
  openFieldDialog() {
    this.resetDialog();
    this.showTypeDialog = true;
  }

  selectType(typeValue: string) {
    this.fieldForm.type = typeValue;
    if (typeValue === 'BOOLEAN') {
      this.fieldForm.options = [{ id: '', text: '', triggersAlert: false, requiresComment: false, position: 1 }];
      return;
    }

    if (
      (typeValue === 'SINGLESELECT' || typeValue === 'MULTISELECT') &&
      this.fieldForm.options.length === 0
    ) {
      this.addOptionInDialog();
    }
  }

  addOptionInDialog() {
    if (this.fieldForm.type === 'BOOLEAN') {
      return;
    }
    this.fieldForm.options.push({
      id: '',
      text: '',
      triggersAlert: false,
      requiresComment: false,
      position: this.fieldForm.options.length + 1,
    });
  }

  removeOptionInDialog(index: number) {
    if (this.fieldForm.type === 'BOOLEAN') {
      return;
    }
    this.fieldForm.options.splice(index, 1);
    this.fieldForm.options.forEach((o, i) => (o.position = i + 1));
  }

  updateBooleanOptionText(value: string) {
    if (this.fieldForm.options.length === 0) {
      this.fieldForm.options = [{ id: '', text: value, triggersAlert: false, requiresComment: false, position: 1 }];
      return;
    }

    this.fieldForm.options[0].text = value;
  }

  toggleRequiresComment(index: number) {
    const options = this.fieldForm.options;
    options[index].requiresComment = !options[index].requiresComment;
    // Se si abilita il commento, non può anche essere SPECIAL_OTHER
    if (options[index].requiresComment && options[index].id === 'SPECIAL_OTHER') {
      options[index].id = '';
      options[index].text = '';
    }
  }

  toggleAltro(index: number) {
    // Sicurezza: permetti l'operazione solo se il tipo è SINGLESELECT
    if (this.fieldForm.type !== 'SINGLESELECT') return;
    const options = this.fieldForm.options;
    const currentOption = options[index];

    if (currentOption.id === 'SPECIAL_OTHER') {
      currentOption.id = '';
      currentOption.text = '';
      return;
    }

    const existingOtherIndex = options.findIndex((o) => o.id === 'SPECIAL_OTHER');
    if (existingOtherIndex !== -1) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Puoi impostare solo un’opzione come campo Altro (testo libero)',
      });
      return;
    }

    currentOption.id = 'SPECIAL_OTHER';
    currentOption.text = 'ALTRO';
    currentOption.triggersAlert = false;
    currentOption.requiresComment = false;
  }

  toggleEnableAlert(index: number) {
    const options = this.fieldForm.options;
    const currentValue = !!options[index]?.triggersAlert;

    if (this.fieldForm.type === 'MULTISELECT' || this.fieldForm.type === 'SINGLESELECT') {
      // MULTISELECT / SINGLESELECT: ogni opzione è indipendente, si possono abilitare più alert
      options[index].triggersAlert = !currentValue;
      return;
    }

    // BOOLEAN: al massimo una opzione può avere Enable Alert = true
    // Se già selezionato, deseleziona tutto
    if (currentValue) {
      options.forEach((option) => { option.triggersAlert = false; });
      return;
    }

    options.forEach((option, idx) => {
      option.triggersAlert = idx === index;
    });
  }

  saveField() {
    if (!this.fieldForm.text?.trim() || !this.fieldForm.type) return;

    if (this.fieldForm.type === 'BOOLEAN') {
      const booleanOptionText = this.fieldForm.options[0]?.text?.trim() || '';
      if (!booleanOptionText) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Attenzione',
          detail: "Per il campo Checkbox devi inserire il testo dell'opzione.",
        });
        return;
      }
      // Garantisce una sola opzione per BOOLEAN
      this.fieldForm.options = [
        {
          ...this.fieldForm.options[0],
          text: booleanOptionText,
        },
      ];
    }

    const isMandatory = this.fieldForm.type === 'MULTISELECT' ? false : this.fieldForm.mandatory;
    const optionsFBArray = this.fb.array(
      this.fieldForm.options
        .filter((opt) => opt.text.trim() !== '')
        .map((o, idx) => {
          // Se l'id è una stringa vuota o un UUID generato dal FE, passiamo null
          // Se invece stiamo editando un questionario esistente e l'id arriva dal BE, lo manteniamo
          const optionId = o.id && o.id !== '' && o.id !== 'SPECIAL_OTHER' ? o.id : null;

          return this.fb.group({
            id: [optionId],
            text: [o.text],
            triggersAlert: [!!o.triggersAlert],
            requiresComment: [!!o.requiresComment],
            position: [o.position ?? idx + 1],
          });
        }),
    );
    if (this.editingIndex !== null) {
      const targetGroup = this.questions.at(this.editingIndex) as FormGroup;
      targetGroup.patchValue({
        text: this.fieldForm.text,
        tooltip: this.fieldForm.tooltip,
        type: this.fieldForm.type,
        mandatory: this.fieldForm.mandatory,
      });
      targetGroup.setControl('options', optionsFBArray);
    } else {
      this.questions.push(
        this.fb.group({
          text: [this.fieldForm.text, Validators.required],
          tooltip: [this.fieldForm.tooltip],
          type: [this.fieldForm.type, Validators.required],
          mandatory: [this.fieldForm.mandatory],
          position: [this.questions.length + 1],
          options: optionsFBArray,
        }),
      );
    }

    this.recalculatePositions();
    this.showTypeDialog = false;
    this.resetDialog();
  }

  editField(index: number) {
    this.editingIndex = index;
    const q = this.questions.at(index).value;
    const currentOptions = this.getOptions(index).controls.map((c) => c.value);

    this.fieldForm = {
      text: q.text,
      tooltip: q.tooltip ?? '',
      type: q.type,
      mandatory: q.mandatory,
      // Esclude opzioni cancellate logicamente (active=false) dall'editor
      options: JSON.parse(JSON.stringify(currentOptions)).filter((o: any) => o.active !== false),
    };

    if (this.fieldForm.type === 'BOOLEAN') {
      const firstOption = this.fieldForm.options[0];
      this.fieldForm.options = [
        {
          id: firstOption?.id || '',
          text: firstOption?.text || '',
          triggersAlert: !!firstOption?.triggersAlert,
          requiresComment: !!firstOption?.requiresComment,
          position: firstOption?.position ?? 1,
        },
      ];
    }

    this.showTypeDialog = true;
  }

  deleteField(index: number) {
    this.questions.removeAt(index);
    this.recalculatePositions();
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.questions.controls, event.previousIndex, event.currentIndex);
    this.recalculatePositions();
  }

  moveUp(index: number) {
    if (index <= 0) return;
    moveItemInArray(this.questions.controls, index, index - 1);
    this.recalculatePositions();
  }

  moveDown(index: number) {
    if (index >= this.questions.controls.length - 1) return;
    moveItemInArray(this.questions.controls, index, index + 1);
    this.recalculatePositions();
  }

  recalculatePositions() {
    this.questions.controls.forEach((q, i) => q.get('position')?.setValue(i + 1));
  }

  dropOption(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.fieldForm.options, event.previousIndex, event.currentIndex);
    this.fieldForm.options.forEach((o, i) => (o.position = i + 1));
  }

  resetDialog() {
    this.fieldForm = {
      text: '',
      tooltip: '',
      type: '',
      mandatory: true,
      options: [],
    };
    this.editingIndex = null;
  }

  submit() {
    if (!this.canSubmit) return;

    // Clone and format dates as YYYY-MM-DD
    const isUpdate = !!this.questionnaireId;
    const payload = {
      ...this.form.value,
      ...(this.form.value.tooltip?.trim() ? { tooltip: this.form.value.tooltip.trim() } : {}),
      questions: (this.form.value.questions || []).map((question: any) => {
        // In update: mantieni questionId per permettere upsert lato BE
        const { id: questionId, ...questionWithoutId } = question;
        const questionBase = isUpdate && questionId ? { id: questionId, ...questionWithoutId } : questionWithoutId;
        return {
          ...questionBase,
          ...(question.tooltip?.trim() ? { tooltip: question.tooltip.trim() } : {}),
          options: (question.type === 'BOOLEAN'
            ? (question.options || []).slice(0, 1)
            : question.options || []
          ).map((option: any) => {
            const { id: optionId, ...optionWithoutId } = option;
            // In update: mantieni optionId quando presente (esistente) per permettere upsert lato BE
            // Senza id → nuova opzione (INSERT), con id → opzione esistente (UPDATE)
            const optionBase = isUpdate && optionId ? { id: optionId, ...optionWithoutId } : optionWithoutId;
            return {
              ...optionBase,
              triggersAlert: !!option.triggersAlert,
              requiresComment: !!option.requiresComment,
            };
          }),
        };
      }),
    };
    if (payload.validFrom instanceof Date) {
      payload.validFrom = payload.validFrom.toISOString().slice(0, 10);
    }
    if (payload.validTo instanceof Date) {
      payload.validTo = payload.validTo.toISOString().slice(0, 10);
    }

    const request$ = this.questionnaireId
      ? this.service.updateQuestionnaire(this.questionnaireId, payload)
      : this.service.createQuestionnaire(payload);

    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Operazione Completata',
          detail: `Il questionario è stato ${this.questionnaireId ? 'aggiornato' : 'creato'} correttamente!`,
          life: 3000,
        });

        setTimeout(() => {
          this.router.navigate(['/questionnaires']);
        }, 1500);
      },
      error: (err) => {
        // Check for duplicate questionnaire error
        if (err.error?.code === 'QUESTIONNAIRE_DUPLICATE' && err.error?.details) {
          const { name, trusteeTypes } = err.error.details;
          const trusteeTypesLabel = Array.isArray(trusteeTypes) ? trusteeTypes.join(', ') : '';
          const errorDetail = `Il questionario con il nome ${name} per i fiduciari ${trusteeTypesLabel} esiste già`;

          this.confirmationService.confirm({
            message: errorDetail,
            header: 'Attenzione',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Ok',
            acceptButtonStyleClass: 'p-button-outlined p-button-primary',
            rejectVisible: false,
            accept: () => {
              // Dialog closes when Ok is clicked
            },
          });
        } else {
          // Default error message using message service
          this.messageService.add({
            severity: 'warn',
            summary: 'Errore',
            detail: 'Si è verificato un errore durante il salvataggio.',
          });
        }
      },
    });
  }

  canSaveField(): boolean {
    if (!this.fieldForm.text?.trim()) {
      return false;
    }

    if (this.fieldForm.type === 'BOOLEAN') {
      return !!this.fieldForm.options[0]?.text?.trim();
    }

    return true;
  }

  retriveTrusteeTypes() {
    this.formDropdownService
      .getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY')
      .subscribe({
        next: (response) => {
          this.trusteeTypes.set(
            (response.data || []).map((item) => ({
              name: item.valueDescription,
              code: item.valueKey,
            })),
          );
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Impossibile caricare le tipologie fiduciario',
          });
        },
      });
  }

  navigateToList(): void {
    this.router.navigate(['/questionnaires']);
  }
}
